class ProductCardInlineQuickShop extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.details = this.querySelector("details");
    this.toggle = this.querySelector("[data-inline-quick-shop-toggle]");
    this.form = this.querySelector(".inline-quick-shop__form");
    this.productForm = this.querySelector("product-form");
    this.variantInput = this.querySelector("[data-inline-variant-id]");
    this.sellingPlanInput = this.querySelector("[data-inline-selling-plan]");
    this.submitButton = this.querySelector('[name="add"]');
    this.submitText = this.querySelector(
      "[data-inline-submit-text], .quick-add-btn-text"
    );
    this.status = this.querySelector("[data-inline-quick-shop-status]");
    this.optionInputs = Array.from(this.querySelectorAll("[data-inline-option]"));
    this.optionGroups = Array.from(this.querySelectorAll("[data-inline-option-group]"));
    this.variants = this.getVariants();
    this.addText = this.dataset.addText || "Add to cart";
    this.addedText = this.dataset.addedText || "Added to cart.";
    this.confirmedText = this.dataset.confirmedText || "Added";
    this.chooseText = this.dataset.chooseText || "Choose options";
    this.soldOutText = this.dataset.soldOutText || "Sold out";
    this.confirmationTimer = null;

    // Pinned option: the card transacts the color it is displaying, so that
    // option has no chips in the panel and its value is supplied here instead.
    this.pinnedOptionIndex =
      this.dataset.pinnedOptionIndex === undefined
        ? null
        : Number(this.dataset.pinnedOptionIndex);
    this.pinnedOptionValue = this.dataset.pinnedOptionValue ?? null;

    this.handleCartSuccess = this.handleCartSuccess.bind(this);
    this.handleCartError = this.handleCartError.bind(this);
    this.suppressCartDrawer = this.suppressCartDrawer.bind(this);

    // Bound before the guard below so they also apply to a single-variant host,
    // which has a <product-form> but no picker. Every card add gets the same
    // inline confirmation and leaves the cart drawer closed.
    this.addEventListener("on:cart:add", this.handleCartSuccess);
    this.addEventListener("on:line-item:change", this.handleCartSuccess);
    this.addEventListener("on:cart:error", this.handleCartError);
    // Capture phase on the host runs before <product-form>'s own submit
    // handler, so the proxy is always in place by the time it reads cartDrawer.
    // Doing it here rather than at connect time makes it independent of
    // custom-element upgrade order, which matters for cards injected as HTML
    // (recently-viewed, facet and pagination re-renders) where the parent
    // upgrades before the nested <product-form>.
    this.addEventListener("submit", this.suppressCartDrawer, true);

    if (!this.details || !this.form || !this.variants.length) return;

    this.handleToggle = this.handleToggle.bind(this);
    this.handleToggleClick = this.handleToggleClick.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);

    this.details.addEventListener("toggle", this.handleToggle);
    this.toggle?.addEventListener("click", this.handleToggleClick);
    this.form.addEventListener("change", this.handleChange);
    this.addEventListener("keydown", this.handleKeydown);

    this.updateSelection();
    this.classList.add("is-ready");
  }

  disconnectedCallback() {
    this.details?.removeEventListener("toggle", this.handleToggle);
    this.toggle?.removeEventListener("click", this.handleToggleClick);
    this.form?.removeEventListener("change", this.handleChange);
    this.removeEventListener("keydown", this.handleKeydown);
    this.removeEventListener("on:cart:add", this.handleCartSuccess);
    this.removeEventListener("on:line-item:change", this.handleCartSuccess);
    this.removeEventListener("on:cart:error", this.handleCartError);
    this.removeEventListener("submit", this.suppressCartDrawer, true);
    window.clearTimeout(this.confirmationTimer);
    if (ProductCardInlineQuickShop.active === this) {
      ProductCardInlineQuickShop.active = null;
    }
    this.initialized = false;
  }

  getVariants() {
    const data = this.querySelector("[data-inline-quick-shop-variants]");
    if (!data) return [];

    try {
      return JSON.parse(data.textContent);
    } catch (error) {
      return [];
    }
  }

  // Adding from a card is a browsing action, not a checkout action: a shopper
  // building a set out of a grid should not have to dismiss a drawer between
  // each add. cart-drawer.js treats the second argument of renderContents as
  // "then open", so passing false still re-renders the whole drawer section —
  // free-shipping progress included — it just does not take over the screen.
  // The PDP's own add-to-cart is untouched and still opens the drawer.
  suppressCartDrawer() {
    const productForm = this.querySelector("product-form");
    const cartDrawer = productForm?.cartDrawer;
    if (!cartDrawer || productForm.inlineCartDrawerProxy) return;

    productForm.inlineCartDrawerProxy = true;
    productForm.cartDrawer = {
      closest: (selector) => cartDrawer.closest(selector),
      renderContents: (data) => cartDrawer.renderContents(data, false)
    };
  }

  handleToggle() {
    const isOpen = this.details.open;
    this.toggle?.setAttribute("aria-expanded", String(isOpen));

    if (!isOpen) {
      if (ProductCardInlineQuickShop.active === this) {
        ProductCardInlineQuickShop.active = null;
      }
      return;
    }

    if (
      ProductCardInlineQuickShop.active &&
      ProductCardInlineQuickShop.active !== this
    ) {
      ProductCardInlineQuickShop.active.close();
    }

    ProductCardInlineQuickShop.active = this;
  }

  handleToggleClick(event) {
    // Some collection/card scripts listen for a bubbled click and navigate to
    // the PDP. The summary's native default action still toggles <details>;
    // stopping propagation keeps Quick Shop inside the card as labelled.
    event.stopPropagation();
  }

  handleChange(event) {
    if (!event.target.matches("[data-inline-option]")) return;
    this.lastChangedOptionIndex = Number(event.target.dataset.optionIndex);
    this.clearStatus();
    this.updateSelection();
  }

  handleKeydown(event) {
    if (event.key !== "Escape" || !this.details.open) return;
    event.preventDefault();
    // Without this the same Escape keeps bubbling to the document-level
    // handlers in cart-drawer/side-drawer/modal.
    event.stopPropagation();
    this.close();
    this.toggle?.focus();
  }

  handleCartSuccess() {
    // Use a short button confirmation plus the complete sentence in the live
    // region. Putting the whole sentence in the button wraps badly on a narrow
    // two-up card, while leaving the label unchanged makes a successful add
    // look like nothing happened.
    window.clearTimeout(this.confirmationTimer);
    this.idleSubmitText =
      this.submitText?.textContent?.trim() || this.addText;

    if (this.status) this.status.textContent = this.addedText;
    if (this.submitText) this.submitText.textContent = this.confirmedText;
    this.submitButton?.classList.add("is-card-confirmed");

    // This class is intentionally independent of product-form.js's generic
    // `is-success`, which it removes after 1400ms. A little over two seconds is
    // long enough to register without holding up someone adding several items.
    this.confirmationTimer = window.setTimeout(() => {
      this.submitButton?.classList.remove("is-card-confirmed");
      if (this.submitText) this.submitText.textContent = this.idleSubmitText;
      // Clear the live region, or it keeps announcing a stale confirmation.
      if (this.status) this.status.textContent = "";
      if (this.details && this.form && this.variants.length) this.updateSelection();
    }, 2200);
  }

  handleCartError() {
    // Errors go to the role="alert" element that product-form.js populates.
    // Writing them here too made screen readers announce the same failure twice.
    // product-form.js adds is-success in a `finally`, so it fires on failures
    // as well — strip it so a failed add never looks like it worked.
    window.clearTimeout(this.confirmationTimer);
    this.submitButton?.classList.remove("is-success", "is-card-confirmed");
    if (this.submitText && this.idleSubmitText) {
      this.submitText.textContent = this.idleSubmitText;
    }
    if (this.status) this.status.textContent = "";
  }

  close() {
    // Closing <details> destroys the panel subtree. If focus was inside it —
    // which happens when one card force-closes another — it resets to <body>
    // and a screen reader's cursor is dumped to the top of the document. Safari
    // and iOS Safari do not move focus to a clicked <summary>, so tapping card
    // B while focus sits on a chip in card A hits this every time.
    if (this.contains(document.activeElement)) this.toggle?.focus();
    this.details.open = false;
  }

  clearStatus() {
    window.clearTimeout(this.confirmationTimer);
    if (this.status) this.status.textContent = "";
    this.submitButton?.classList.remove("is-success", "is-card-confirmed");
    if (this.submitText && this.idleSubmitText) {
      this.submitText.textContent = this.idleSubmitText;
    }
  }

  // Must be indexed by OPTION index, not by group order. updateOptionAvailability
  // already reads it that way (`variant.options[selectedIndex]`), and the two
  // only agreed while every option rendered a group. Now that the pinned color
  // option renders no chips, the array is built to full length and the pinned
  // value is filled in, so Size still resolves against the displayed color.
  getSelectedOptions() {
    const optionCount = this.variants[0]?.options.length || this.optionGroups.length;
    const values = new Array(optionCount).fill(null);

    if (this.pinnedOptionIndex !== null && this.pinnedOptionValue !== null) {
      values[this.pinnedOptionIndex] = this.pinnedOptionValue;
    }

    this.optionGroups.forEach((group) => {
      const index = Number(group.dataset.optionIndex);
      const select = group.querySelector("select[data-inline-option]");
      if (select) {
        if (select.value) values[index] = select.value;
        return;
      }

      const selected = group.querySelector("[data-inline-option]:checked");
      if (selected) values[index] = selected.value;
    });

    return values;
  }

  updateOptionAvailability(selectedOptions) {
    let selectionCleared = false;

    this.optionInputs.forEach((input) => {
      const optionIndex = Number(input.dataset.optionIndex);
      const isSelect = input.tagName === "SELECT";

      if (isSelect) {
        Array.from(input.options).forEach((option) => {
          if (!option.value) return;

          const available = this.isOptionValueAvailable(
            optionIndex,
            option.value,
            selectedOptions,
            input.value === option.value
          );
          const label = option.dataset.optionLabel || option.value;

          option.disabled = !available;
          // A native select already makes the shopper open a second surface to
          // choose a value. Do not make them scan inventory they cannot buy.
          // Keep disabled as a safety net and hide the unavailable entry from
          // the platform picker entirely.
          option.hidden = !available;
          option.textContent = label;
        });

        if (input.value && input.options[input.selectedIndex]?.disabled) {
          input.value = "";
          selectionCleared = true;
        }
        return;
      }

      const available = this.isOptionValueAvailable(
        optionIndex,
        input.value,
        selectedOptions,
        input.checked
      );

      input.disabled = !available;
      input.setAttribute(
        "aria-label",
        available
          ? input.dataset.optionLabel
          : `${input.dataset.optionLabel}, ${this.soldOutText}`
      );
      const label = input.nextElementSibling;
      input.hidden = !available;
      if (label) {
        label.hidden = !available;
        label.classList.toggle("is-unavailable", !available);
      }

      if (input.checked && input.disabled) {
        input.checked = false;
        selectionCleared = true;
      }
    });

    return selectionCleared;
  }

  isOptionValueAvailable(
    optionIndex,
    optionValue,
    selectedOptions,
    isCurrentSelection
  ) {
    return this.variants.some((variant) => {
      if (!variant.available || variant.options[optionIndex] !== optionValue) {
        return false;
      }

      return selectedOptions.every((selected, selectedIndex) => {
        if (selectedIndex === optionIndex || selected === null) return true;
        if (isCurrentSelection && optionIndex === this.lastChangedOptionIndex) {
          return true;
        }
        return variant.options[selectedIndex] === selected;
      });
    });
  }

  updateSelection() {
    let selectedOptions = this.getSelectedOptions();
    const selectionCleared = this.updateOptionAvailability(selectedOptions);

    if (selectionCleared) {
      selectedOptions = this.getSelectedOptions();
      this.updateOptionAvailability(selectedOptions);
    }
    this.lastChangedOptionIndex = null;

    const allOptionsSelected = selectedOptions.every((value) => value !== null);
    const variant = allOptionsSelected
      ? this.variants.find((item) =>
          item.options.every(
            (option, index) => option === selectedOptions[index]
          )
        )
      : null;

    const canAdd =
      Boolean(variant?.available) &&
      (!this.sellingPlanInput || Boolean(variant.sellingPlanId));

    this.variantInput.value = canAdd ? variant.id : "";
    this.variantInput.disabled = !canAdd;

    if (this.sellingPlanInput) {
      this.sellingPlanInput.value = canAdd ? variant.sellingPlanId : "";
      this.sellingPlanInput.disabled = !canAdd;
    }

    this.submitButton.disabled = !canAdd;
    // No price on the button. It came from the SELECTED variant while the card's
    // price block above it comes from the CHEAPEST one with a "From" prefix
    // (snippets/price.liquid), so a 2oz/4oz/10oz product read "From $12.00"
    // above a button saying "$28.00" — and where a compare-at sale applied only
    // to the cheapest variant, the button contradicted a strikethrough the
    // shopper thought they were getting. The separator was also an em dash,
    // which breaks hard rule 8 of the brand voice guide (ASCII punctuation
    // only), and the extra glyphs forced a second line on narrow cards.
    this.submitText.textContent = canAdd ? this.addText : this.chooseText;
  }
}

ProductCardInlineQuickShop.active = null;

if (!customElements.get("inline-quick-shop")) {
  customElements.define("inline-quick-shop", ProductCardInlineQuickShop);
}
